import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { ArrowLeft, Send } from 'lucide-react';

const CompanyPostProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    function: '',
    description: '',
    responsibilities: '',
    ideal_candidate: '',
    duration: '',
    hours_per_week: '',
    budget_range: '',
    location_type: '',
    timezone: '',
    tools_required: '',
    start_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.function || !formData.description || !formData.duration || !formData.hours_per_week || !formData.location_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/projects`, {
        ...formData,
        hours_per_week: parseInt(formData.hours_per_week)
      });
      toast.success('Project submitted for review!');
      navigate('/company/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="post-project-page">
      <Header />

      <div className="container-custom py-12 max-w-3xl">
        <button
          onClick={() => navigate('/company/projects')}
          className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Projects
        </button>

        <div className="mb-8">
          <h1 className="heading-2 mb-2">Post a New Project</h1>
          <p className="text-slate-600">Create a detailed project brief to attract the right talent</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="p-8 mb-6" data-testid="project-basics-card">
            <h2 className="heading-4 mb-6">Project Basics</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Senior Product Manager - Growth"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Function *</Label>
                  <Select value={formData.function} onValueChange={(v) => setFormData({ ...formData, function: v })}>
                    <SelectTrigger data-testid="function-select">
                      <SelectValue placeholder="Select function" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Product</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="engineering">Engineering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Location Type *</Label>
                  <Select value={formData.location_type} onValueChange={(v) => setFormData({ ...formData, location_type: v })}>
                    <SelectTrigger data-testid="location-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="onsite">On-site</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Duration *</Label>
                  <Select value={formData.duration} onValueChange={(v) => setFormData({ ...formData, duration: v })}>
                    <SelectTrigger data-testid="duration-select">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 month">1 month</SelectItem>
                      <SelectItem value="2 months">2 months</SelectItem>
                      <SelectItem value="3 months">3 months</SelectItem>
                      <SelectItem value="4 months">4 months</SelectItem>
                      <SelectItem value="6 months">6 months</SelectItem>
                      <SelectItem value="Ongoing">Ongoing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Hours per Week *</Label>
                  <Select value={formData.hours_per_week} onValueChange={(v) => setFormData({ ...formData, hours_per_week: v })}>
                    <SelectTrigger data-testid="hours-select">
                      <SelectValue placeholder="Select hours" />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 15, 20, 25, 30, 35, 40].map((h) => (
                        <SelectItem key={h} value={h.toString()}>{h} hours</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    placeholder="e.g., US Pacific"
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    data-testid="timezone-input"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="project-details-card">
            <h2 className="heading-4 mb-6">Project Details</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Project Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the project, its goals, and what you're looking to achieve..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="min-h-[150px]"
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsibilities">Key Responsibilities</Label>
                <Textarea
                  id="responsibilities"
                  placeholder="- Define and execute strategy&#10;- Work with cross-functional teams&#10;- Report on key metrics"
                  value={formData.responsibilities}
                  onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                  className="min-h-[120px]"
                  data-testid="responsibilities-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ideal_candidate">Ideal Candidate</Label>
                <Textarea
                  id="ideal_candidate"
                  placeholder="Describe the ideal candidate's background, skills, and experience..."
                  value={formData.ideal_candidate}
                  onChange={(e) => setFormData({ ...formData, ideal_candidate: e.target.value })}
                  className="min-h-[120px]"
                  data-testid="ideal-candidate-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tools">Tools & Skills Required</Label>
                <Input
                  id="tools"
                  placeholder="e.g., Figma, React, PostgreSQL, Amplitude"
                  value={formData.tools_required}
                  onChange={(e) => setFormData({ ...formData, tools_required: e.target.value })}
                  data-testid="tools-input"
                />
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="project-budget-card">
            <h2 className="heading-4 mb-6">Budget & Timeline</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Range (Hidden from talent)</Label>
                <Input
                  id="budget"
                  placeholder="e.g., $100-150/hr"
                  value={formData.budget_range}
                  onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                  data-testid="budget-input"
                />
                <p className="text-xs text-slate-500">This will only be visible to you and our admin team</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date">Desired Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/company/projects')}>
              Cancel
            </Button>
            <Button type="submit" className="btn-accent" disabled={loading} data-testid="submit-project-btn">
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Submitting...' : 'Submit for Review'}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CompanyPostProject;
